import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import apiClient, { setDynamoPreviewEnabled, setClientRole, setRoleMismatchHandler } from '../api'; // Your configured axios instance
import { useNavigate } from 'react-router-dom';
import { SESSION_TIMEOUTS } from '../utils/constants';
import SessionTimeoutWarning from '../components/SessionTimeoutWarning';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('authToken')); // Initialize from localStorage
    const [loading, setLoading] = useState(true); // Start loading until check is complete
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Session timeout state
    const [sessionMetadata, setSessionMetadata] = useState(null);
    const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
    const [timeoutCountdown, setTimeoutCountdown] = useState(0);
    const idleTimeoutRef = useRef(null);
    const warningTimeoutRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    // Function to update state (cookies are handled by server)
    const setAuthState = (newUser, sessionData = null) => {
        if (newUser) {
            localStorage.setItem('authUser', JSON.stringify(newUser));
            setClientRole(newUser.role);
            setToken('cookie-based'); // Indicate cookie-based auth
            setUser(newUser);
            setError(null); // Clear previous errors on successful login
            setDynamoPreviewEnabled(false);
            
            if (sessionData) {
                setSessionMetadata(sessionData);
                localStorage.setItem('sessionMetadata', JSON.stringify(sessionData));
            }
        } else {
            localStorage.removeItem('authUser');
            localStorage.removeItem('sessionMetadata');
            setClientRole('');
            setToken(null);
            setUser(null);
            setSessionMetadata(null);
            setDynamoPreviewEnabled(false);
            clearSessionTimeouts();
        }
        setLoading(false); // Ensure loading is set to false after state change
    };

    // Session timeout functions
    const clearSessionTimeouts = useCallback(() => {
        if (idleTimeoutRef.current) {
            clearTimeout(idleTimeoutRef.current);
            idleTimeoutRef.current = null;
        }
        if (warningTimeoutRef.current) {
            clearTimeout(warningTimeoutRef.current);
            warningTimeoutRef.current = null;
        }
        setShowTimeoutWarning(false);
    }, []);

    const setupSessionTimeouts = useCallback((metadata) => {
        clearSessionTimeouts();
        
        if (!metadata) return;

        const loginTime = new Date(metadata.loginTime);
        const expiresAt = new Date(metadata.expiresAt);
        const isRememberMe = metadata.isRememberMe;

        // Check absolute timeout
        const absoluteTimeout = isRememberMe ? SESSION_TIMEOUTS.REMEMBER_ME_TIMEOUT : SESSION_TIMEOUTS.ABSOLUTE_TIMEOUT;
        const absoluteExpiry = new Date(loginTime.getTime() + absoluteTimeout);
        
        if (Date.now() > absoluteExpiry.getTime()) {
            logout();
            return;
        }

        // Remember-me sessions skip short idle auto-logout (drivers often leave
        // the phone overnight). Absolute timeout still applies via cookie/JWT.
        if (isRememberMe) {
            return () => {
                clearSessionTimeouts();
            };
        }

        // Setup idle timeout (non-remember sessions only)
        const resetIdleTimeout = () => {
            clearSessionTimeouts();
            lastActivityRef.current = Date.now();
            
            const idleTimeout = setTimeout(() => {
                // Show warning 3 minutes before logout
                setShowTimeoutWarning(true);
                setTimeoutCountdown(SESSION_TIMEOUTS.WARNING_BEFORE_LOGOUT / 1000);
                
                // Auto-logout after warning period
                warningTimeoutRef.current = setTimeout(() => {
                    logout();
                }, SESSION_TIMEOUTS.WARNING_BEFORE_LOGOUT);
            }, SESSION_TIMEOUTS.IDLE_TIMEOUT - SESSION_TIMEOUTS.WARNING_BEFORE_LOGOUT);
            
            idleTimeoutRef.current = idleTimeout;
        };

        resetIdleTimeout();

        // Add activity listeners
        const handleActivity = () => {
            lastActivityRef.current = Date.now();
            resetIdleTimeout();
        };

        SESSION_TIMEOUTS.ACTIVITY_EVENTS.forEach(event => {
            document.addEventListener(event, handleActivity, true);
        });

        // Cleanup function
        return () => {
            SESSION_TIMEOUTS.ACTIVITY_EVENTS.forEach(event => {
                document.removeEventListener(event, handleActivity, true);
            });
            clearSessionTimeouts();
        };
    }, [clearSessionTimeouts]);

    const handleStayLoggedIn = useCallback(async () => {
        try {
            const response = await apiClient.post('/auth/refresh');
            if (response.data.status === 'success') {
                const { sessionMetadata: newMetadata } = response.data;
                
                if (newMetadata) {
                    setSessionMetadata(newMetadata);
                    localStorage.setItem('sessionMetadata', JSON.stringify(newMetadata));
                    setupSessionTimeouts(newMetadata);
                }
                
                setShowTimeoutWarning(false);
                setTimeoutCountdown(0);
            }
        } catch (error) {
            console.error('Failed to refresh token:', error);
            logout();
        }
    }, [setupSessionTimeouts]);

    const applyLoginSuccess = useCallback((response) => {
        const { data: { user: loggedInUser, readStore }, passwordResetRequired, sessionMetadata } = response.data;
        setAuthState({ ...loggedInUser, readStore: readStore || 'mongo' }, sessionMetadata);
        if (sessionMetadata) {
            setupSessionTimeouts(sessionMetadata);
        }
        if (passwordResetRequired) {
            return { success: true, requirePasswordChange: true, role: loggedInUser?.role };
        }
        return { success: true, role: loggedInUser?.role };
    }, [setupSessionTimeouts]);

    // Login function
    const login = useCallback(async (email, password, rememberMe = false) => {
        setLoading(true);
        setError(null);
        try {
            console.log(`[AuthContext] Attempting login with email: ${email}, rememberMe: ${rememberMe}`);
            const response = await apiClient.post('/auth/login', { email, password, rememberMe });

            if (response.data?.requiresTwoFactor) {
                setLoading(false);
                return {
                    success: false,
                    requiresTwoFactor: true,
                    pendingToken: response.data.pendingToken,
                    emailHint: response.data.emailHint
                };
            }
            
            if (response.data.status === 'success') {
                return applyLoginSuccess(response);
            } else {
                console.error('[AuthContext] Login failed with unexpected response structure:', response.data);
                throw new Error(response.data.message || 'Login failed with unexpected server response');
            }
        } catch (err) {
            console.error('[AuthContext] Login API error:', err);
            
            // Determine error type for better UI handling
            let errorType = 'unknown';
            let errorMessage = 'An unexpected error occurred. Please try again.';
            
            if (err.message === 'Network Error') {
                errorType = 'network';
                errorMessage = 'Connection error. Please check your internet and try again.';
            } else if (err.response) {
                // Server responded with an error
                const status = err.response.status;
                // Try to get the error message from different possible locations in the response
                const serverMessage = err.response?.data?.message || 
                                    err.response?.data?.error || 
                                    err.response?.data?.details ||
                                    null;
                
                console.log('[AuthContext] Server response details:', { 
                    status, 
                    data: err.response.data,
                    message: serverMessage 
                });
                
                // Categorize common errors
                if (status === 401) {
                    errorType = 'credentials';
                    errorMessage = serverMessage || 'Invalid email or password. Please try again.';
                } else if (status === 403) {
                    // Check if this is a password reset required response
                    if (err.response?.data?.requiresPasswordReset) {
                        errorType = 'password_reset_required';
                        errorMessage = serverMessage || 'Your password must be reset to meet new security requirements. A reset link has been sent to your email.';
                    } else {
                        errorType = 'credentials';
                        errorMessage = serverMessage || 'Invalid email or password. Please try again.';
                    }
                } else if (status === 423) {
                    errorType = 'account_locked';
                    // Use the detailed message from the server which includes the lock duration
                    errorMessage = serverMessage || 'Your account has been locked due to too many failed login attempts.';
                } else if (status === 429) {
                    errorType = 'rate_limit';
                    errorMessage = serverMessage || 'Too many login attempts from this IP. Please try again later.';
                } else {
                    errorType = 'server';
                    errorMessage = serverMessage || 'Server error. Please try again later.';
                }
            }
            
            // Make sure error message is not empty or undefined
            if (!errorMessage || errorMessage.trim() === '') {
                errorMessage = 'Authentication failed. Please try again.';
            }
            
            // Set the error in state
            setError(errorMessage);
            // Don't clear auth state here - it causes the login page to unmount
            // Just set loading to false
            setLoading(false); // Ensure loading is false on error
            
            console.log(`[AuthContext] Returning error: ${errorType} - ${errorMessage}`);
            
            // Return structured error instead of throwing
            return {
                success: false,
                errorType,
                error: errorMessage
            };
        }
    }, [navigate, applyLoginSuccess]);

    const completeLoginCode = useCallback(async (pendingToken, code) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.post('/auth/verify-login-code', { pendingToken, code });
            if (response.data.status === 'success') {
                return applyLoginSuccess(response);
            }
            throw new Error(response.data.message || 'Incorrect code');
        } catch (err) {
            setLoading(false);
            const message = err.response?.data?.message || err.message || 'Incorrect code';
            return { success: false, error: message };
        }
    }, [applyLoginSuccess]);

    const resendLoginCode = useCallback(async (pendingToken) => {
        const response = await apiClient.post('/auth/resend-login-code', { pendingToken });
        return response.data;
    }, []);

    // Logout function
    const logout = useCallback(async () => {
        console.log('[AuthContext] Logging out...');
        clearSessionTimeouts();
        
        try {
            // Call logout endpoint to clear server-side cookie
            await apiClient.post('/auth/logout');
        } catch (error) {
            console.error('Error during logout:', error);
            // Continue with logout even if server call fails
        }
        
        // Clear auth storage and cookies (preserve user preferences like vehicle type onboarding)
        console.log('[AuthContext] Clearing auth storage and cookies...');
        try {
            // Remove only auth-related keys from localStorage
            try { localStorage.removeItem('authUser'); } catch (_) {}
            try { localStorage.removeItem('sessionMetadata'); } catch (_) {}
            try { localStorage.removeItem('authToken'); } catch (_) {}
            
            // Clear sessionStorage (short-lived session data)
            try { sessionStorage.clear(); } catch (_) {}
            
            // Clear all cookies
            document.cookie.split(";").forEach(function(c) { 
                document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
            });
            
            console.log('[AuthContext] Auth storage and cookies cleared');
        } catch (error) {
            console.error('[AuthContext] Error clearing auth storage:', error);
            // Continue with logout even if storage clearing fails
        }
        
        setAuthState(null, null);
        navigate('/login');
    }, [navigate, clearSessionTimeouts]);

    // Update user profile function
    const updateUserProfile = useCallback(async (profileData) => {
        setLoading(true);
        setError(null);
        try {
            console.log('[AuthContext] Updating user profile...');
            
            // Handle password updates separately
            let updateData = { ...profileData };
            
            // Don't send password data if it's empty
            if (updateData.newPassword && !updateData.currentPassword) {
                setError('Current password is required to update password');
                setLoading(false);
                return { success: false, error: 'Current password is required' };
            }
            
            const response = await apiClient.patch('/users/profile', updateData);
            
            if (response.data.success) {
                console.log('[AuthContext] Profile updated successfully');
                
                // Update user in state and localStorage
                const updatedUser = response.data.data.user;
                
                // Update the local storage with updated user data
                if (updatedUser) {
                    localStorage.setItem('authUser', JSON.stringify(updatedUser));
                    setClientRole(updatedUser.role);
                    setUser(updatedUser);
                }
                
                setLoading(false);
                return { success: true };
            } else {
                throw new Error(response.data.message || 'Failed to update profile');
            }
        } catch (err) {
            console.error('[AuthContext] Profile update error:', err);
            console.error('[AuthContext] Response data:', err.response?.data);
            
            // Extract error message from response if available
            const errorMessage = err.response?.data?.message || err.message || 'Failed to update profile';
            setError(errorMessage);
            setLoading(false);
            
            return { 
                success: false, 
                error: errorMessage
            };
        }
    }, []);

    // Set auth state after password reset (for auto-login)
    const setAuthAfterReset = useCallback((newUser) => {
        localStorage.setItem('authUser', JSON.stringify(newUser));
        setClientRole(newUser.role);
        setUser(newUser);
        setToken('cookie-based'); // Indicate cookie-based auth
        setError(null);
        setLoading(false);
        setDynamoPreviewEnabled(false);
        console.log('[AuthContext] Auth state updated after password reset');
        return { success: true };
    }, []);

    const syncSessionUser = useCallback(async ({ allowClear = false } = {}) => {
        try {
            const response = await apiClient.get('/auth/verify');
            if (response.data?.status !== 'success') {
                throw new Error('Session verification failed');
            }
            const serverUser = response.data?.data?.user;
            if (!serverUser) {
                throw new Error('Session verification failed');
            }
            const readStore = response.data?.data?.readStore || serverUser.readStore || 'mongo';
            const storedSessionMetadata = localStorage.getItem('sessionMetadata');
            const parsedSessionMetadata = storedSessionMetadata ? JSON.parse(storedSessionMetadata) : sessionMetadata;
            setAuthState({ ...serverUser, readStore }, parsedSessionMetadata || null);
            if (parsedSessionMetadata) {
                setupSessionTimeouts(parsedSessionMetadata);
            }
            return serverUser;
        } catch (verifyError) {
            if (allowClear) {
                setAuthState(null, null);
            }
            throw verifyError;
        }
    }, [sessionMetadata, setupSessionTimeouts]);

    // Initial check on component mount
    useEffect(() => {
        const checkAuth = async () => {
            const storedUser = localStorage.getItem('authUser');

            if (storedUser) {
                try {
                    JSON.parse(storedUser);
                    try {
                        await syncSessionUser({ allowClear: true });
                    } catch (verifyError) {
                        console.warn('[AuthContext] Session verification failed', verifyError.response?.status || verifyError.message);
                        setAuthState(null, null);
                    }
                } catch (e) {
                    console.error('[AuthContext] Failed to parse stored user. Clearing auth state.', e);
                    setAuthState(null, null); // Clear state if user data is corrupt
                }
            } else {
                setAuthState(null, null); // Ensure state is cleared if nothing is found
            }
            setLoading(false); // Finished initial check
        };
        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only once on mount

    // Keep client role in sync when the server role changes mid-session.
    useEffect(() => {
        let pending = false;
        const refresh = () => {
            if (pending || !user) return;
            pending = true;
            syncSessionUser()
                .catch(() => {})
                .finally(() => { pending = false; });
        };
        setRoleMismatchHandler(refresh);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            setRoleMismatchHandler(null);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [user, syncSessionUser]);

    const setDynamoPreview = useCallback(async (on) => {
        const response = await apiClient.patch('/admin/datastore', { readStore: on ? 'dynamo' : 'mongo' });
        const next = response.data?.data?.readStore === 'dynamo' ? 'dynamo' : 'mongo';
        setUser((prev) => {
            if (!prev) return prev;
            const updated = { ...prev, readStore: next, dynamoPreview: next === 'dynamo' };
            localStorage.setItem('authUser', JSON.stringify(updated));
            return updated;
        });
        return next === 'dynamo';
    }, []);

    const value = useMemo(() => ({
        user,
        token,
        isAuthenticated: !!token && !!user,
        loading,
        isLoading: loading, // Export both for compatibility
        error,
        login,
        completeLoginCode,
        resendLoginCode,
        logout,
        updateUserProfile,
        setAuthAfterReset,
        setDynamoPreview,
        sessionMetadata,
        showTimeoutWarning,
        timeoutCountdown,
        handleStayLoggedIn
    }), [user, token, loading, error, login, completeLoginCode, resendLoginCode, logout, updateUserProfile, setAuthAfterReset, setDynamoPreview, sessionMetadata, showTimeoutWarning, timeoutCountdown, handleStayLoggedIn]);

    // Always render children - don't unmount them when loading changes
    // This prevents losing component state (like error messages) during auth operations
    return (
        <AuthContext.Provider value={value}>
            {children}
            <SessionTimeoutWarning 
                open={showTimeoutWarning}
                onStayLoggedIn={handleStayLoggedIn}
                onLogout={logout}
                timeRemaining={timeoutCountdown}
            />
        </AuthContext.Provider>
    );
};

// Hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 